import { Input } from "@/components/ui/input"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  SettingsField,
  SettingsPage,
  SettingsSection,
} from "../../components/SettingsPage"
import { SettingsWithPreview } from "../../components/SettingsPreview"
import { TemplateSaveButton } from "../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../components/TemplateSettingsLoader"
import { useSavedDraft } from "../../hooks/useSavedDraft"
import { useTemplateSettingsSave } from "../../hooks/useTemplateSettingsSave"
import { ArticleFieldsPreview } from "../../previews/ArticleFieldsPreview"

function FieldLabelsForm({ saved }: { saved: InquiryTemplateSettings }) {
  const { translate } = useTranslation()
  const saver = useTemplateSettingsSave()
  const [productGroupLabel, setProductGroupLabel] = useSavedDraft(
    saved.productGroupLabel,
  )

  return (
    <SettingsSection
      title={translate("SETTINGS/MASTER_DATA/LABELS_TITLE")}
      description={translate("MC_TPL_MD_DESC")}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="product-group-label"
          // The backend trims too; blank clears the label.
          dirty={productGroupLabel.trim() !== saved.productGroupLabel}
          patch={() => ({ productGroupLabel: productGroupLabel.trim() })}
        />
      }
    >
      <SettingsWithPreview
        preview={<ArticleFieldsPreview productGroupLabel={productGroupLabel} />}
      >
        <SettingsField
          htmlFor="settings-product-group-label"
          label={translate("MC_TPL_DECL_PRODUCT_GROUP_LABEL")}
          hint={translate("MC_TPL_DECL_PRODUCT_GROUP_LABEL_HINT")}
        >
          <Input
            id="settings-product-group-label"
            value={productGroupLabel}
            disabled={!saver.canEdit}
            placeholder={translate("MC_MD_FIELD_PRODUCT_GROUP")}
            onChange={(event) => setProductGroupLabel(event.target.value)}
            className="sm:max-w-xs"
          />
        </SettingsField>
      </SettingsWithPreview>
    </SettingsSection>
  )
}

/** How master data is labelled in the organization. */
export function MasterDataPage() {
  const { translate } = useTranslation()
  return (
    <SettingsPage
      title={translate("SETTINGS/MASTER_DATA/TITLE")}
      description={translate("SETTINGS/MASTER_DATA/DESCRIPTION")}
      notice={translate("SETTINGS/GENERAL/NOTICE")}
      width="wide"
    >
      <TemplateSettingsLoader>
        {(settings) => <FieldLabelsForm saved={settings} />}
      </TemplateSettingsLoader>
    </SettingsPage>
  )
}
